/* eslint-disable react-refresh/only-export-components -- intentional: pure filter helpers co-located with the filter component */
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Grid3x3, List, Star, Box, Sparkles, ImageIcon, Sparkle } from 'lucide-react';

import type { AvatarItem } from '@/services/avatarApi';
import { deriveAvatarFlags } from '@/components/avatars/AvatarBadges';
import { Button } from '@/components/common/Button';
import { cn } from '@/lib/cn';

export type AvatarFilter = 'all' | '3d' | 'animated' | 'featured' | 'photo';
export type AvatarView = 'grid' | 'list';

interface AvatarFiltersProps {
  items: AvatarItem[];
  activeFilter: AvatarFilter;
  view: AvatarView;
  onFilterChange: (f: AvatarFilter) => void;
  onViewChange: (v: AvatarView) => void;
}

/**
 * Counts per filter dimension. Derived once at the parent (Avatars.tsx) and
 * passed to the chips so they can render `(N)` next to the label and skip
 * filters that would yield an empty list.
 */
export interface FilterCounts {
  total: number;
  threeD: number;
  animated: number;
  featured: number;
  photo: number;
}

export function computeFilterCounts(items: AvatarItem[]): FilterCounts {
  let threeD = 0;
  let animated = 0;
  let featured = 0;
  let photo = 0;
  for (const a of items) {
    const f = deriveAvatarFlags(a);
    if (f.has3d || f.inFlightMesh) threeD++;
    if (f.hasAnimation) animated++;
    if (f.isFeatured) featured++;
    if (f.isPhotoBase) photo++;
  }
  return { total: items.length, threeD, animated, featured, photo };
}

export function applyAvatarFilter(items: AvatarItem[], filter: AvatarFilter): AvatarItem[] {
  if (filter === 'all') return items;
  return items.filter((a) => {
    const f = deriveAvatarFlags(a);
    switch (filter) {
      case '3d':
        return f.has3d || f.inFlightMesh;
      case 'animated':
        return f.hasAnimation;
      case 'featured':
        return f.isFeatured;
      case 'photo':
        return f.isPhotoBase;
      default:
        return true;
    }
  });
}

const CHIPS: Array<{ key: AvatarFilter; icon: ReactElement; countKey: keyof FilterCounts }> = [
  { key: 'all',      icon: <Sparkle aria-hidden className="h-3.5 w-3.5" />,     countKey: 'total' },
  { key: '3d',       icon: <Box aria-hidden className="h-3.5 w-3.5" />,         countKey: 'threeD' },
  { key: 'animated', icon: <Sparkles aria-hidden className="h-3.5 w-3.5" />,    countKey: 'animated' },
  { key: 'featured', icon: <Star aria-hidden className="h-3.5 w-3.5" />,        countKey: 'featured' },
  { key: 'photo',    icon: <ImageIcon aria-hidden className="h-3.5 w-3.5" />,   countKey: 'photo' },
];

export function AvatarFilters({
  items,
  activeFilter,
  view,
  onFilterChange,
  onViewChange,
}: AvatarFiltersProps) {
  const { t } = useTranslation('avatars');
  const counts = computeFilterCounts(items);
  const chipLabels: Record<AvatarFilter, string> = {
    all: t('filters.chip.all'),
    '3d': t('filters.chip.threeD'),
    animated: t('filters.chip.animated'),
    featured: t('filters.chip.hub'),
    photo: t('filters.chip.photo'),
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] uppercase tracking-wide text-fg-muted">
          <span className="font-bold text-fg">{counts.total}</span> {t('filters.avatarsLabel', { count: counts.total })}
          {counts.threeD > 0 && (
            <>
              {' · '}
              <span className="font-bold text-fg">{counts.threeD}</span> {t('filters.chip.threeD')}
            </>
          )}
          {counts.animated > 0 && (
            <>
              {' · '}
              <span className="font-bold text-fg">{counts.animated}</span> {t('filters.animatedLabel', { count: counts.animated })}
            </>
          )}
          {counts.featured > 0 && (
            <>
              {' · '}
              <span className="font-bold text-fg">{counts.featured}</span> {t('filters.chip.hub')}
            </>
          )}
        </p>
        <div role="group" aria-label={t('filters.viewMode')} className="flex items-center gap-0.5 rounded-full bg-bg-secondary p-0.5">
          {(['grid', 'list'] as const).map((v) => (
            <button
              key={v}
              type="button"
              aria-pressed={view === v}
              onClick={() => onViewChange(v)}
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full transition-colors',
                view === v ? 'bg-brand text-brand-fg' : 'text-fg-muted',
              )}
            >
              {v === 'grid' ? (
                <Grid3x3 aria-hidden className="h-3.5 w-3.5" />
              ) : (
                <List aria-hidden className="h-3.5 w-3.5" />
              )}
            </button>
          ))}
        </div>
      </div>
      <div className="no-scrollbar -mx-4 flex gap-1.5 overflow-x-auto px-4">
        {CHIPS.map((chip) => {
          const n = counts[chip.countKey];
          // Hide empty filters (except All) so we don't show "0" chips.
          if (chip.key !== 'all' && n === 0) return null;
          const active = activeFilter === chip.key;
          return (
            <Button
              key={chip.key}
              variant="scelta"
              pressed={active}
              className="shrink-0 gap-1.5"
              onClick={() => onFilterChange(chip.key)}
            >
              {chip.icon}
              <span>{chipLabels[chip.key]}</span>
              <span className={cn('font-bold', active ? 'text-brand-fg/80' : 'text-fg-muted')}>{n}</span>
            </Button>
          );
        })}
      </div>
    </div>
  );
}
